/**
 * Maps Flipkart Marketplace shipment / order-item payloads (v3) into OMS create-order field keys.
 * @see https://flipkart.github.io/fk-api-platform-docs/docs/mp-api_versioned/get-shipment-details-by-internal-id
 */

const FK_TO_OMS_STATUS = {
  APPROVED: "New",
  PACKING_IN_PROGRESS: "Confirmed",
  FORM_FAILED: "Confirmed",
  PACKED: "Packed",
  READY_TO_DISPATCH: "Packed",
  PICKUP_COMPLETE: "Shipped",
  SHIPPED: "Shipped",
  DELIVERED: "Delivered",
  COMPLETED: "Delivered",
  CANCELLED: "Cancelled",
  RETURN_REQUESTED: "Confirmed",
  RETURNED: "Delivered",
};

function firstShipment(payload) {
  const list = payload?.shipments;
  if (Array.isArray(list) && list.length) return list[0];
  return null;
}

function firstOrderItem(shipment) {
  const items = shipment?.orderItems;
  if (Array.isArray(items) && items.length) return items[0];
  return null;
}

function pickAddressLines(addr) {
  if (!addr || typeof addr !== "object") return { line: "", city: "", state: "", pincode: "", country: "India" };
  const line =
    [addr.addressLine1, addr.addressLine2, addr.landmark].filter(Boolean).join(", ") ||
    addr.fullAddress ||
    "";
  return {
    line,
    city: addr.city || "",
    state: addr.state || "",
    pincode: addr.pincode || addr.postalCode || "",
    country: addr.country || "India",
  };
}

/**
 * @param {object} apiResponse - Body from GET /sellers/v3/shipments?orderIds=...
 * @returns {Record<string, unknown>} Partial OMS form object
 */
export function mapFlipkartShipmentsToOmsForm(apiResponse) {
  const shipment = firstShipment(apiResponse);
  const item = firstOrderItem(shipment);

  const shipping = shipment?.shippingAddress || shipment?.deliveryAddress || item?.shippingAddress;
  const billing = shipment?.billingAddress || item?.billingAddress;
  const shipLines = pickAddressLines(shipping);
  const billLines = pickAddressLines(billing);

  const buyer = shipment?.buyerDetails || item?.buyerDetails || {};
  const pc = item?.priceComponents || {};

  const fkStatus = item?.status || "";
  const omsStatus = FK_TO_OMS_STATUS[fkStatus] || "New";

  const orderDateIso = item?.orderDate || shipment?.dispatchByDate || "";

  return {
    marketplaceName: "Flipkart",
    marketplaceOrderId: item?.orderId || "",
    orderType: "Sales Order",
    orderDate: orderDateIso || undefined,
    orderStatus: omsStatus,
    orderPriority: "Normal",
    fulfillmentType:
      item?.serviceProfile === "Seller_Fulfilment" || item?.serviceProfile === "NON_FBF"
        ? "Seller Fulfilled"
        : "Marketplace",
    paymentMode: item?.paymentType === "COD" ? "COD" : item?.paymentType === "PREPAID" ? "Prepaid" : "",
    paymentStatus: item?.paymentType === "PREPAID" ? "Paid" : "Pending",
    currency: "INR",

    customerName: [buyer.firstName, buyer.lastName].filter(Boolean).join(" ") || buyer.name || "",
    customerEmail: buyer.email || "",
    customerPhone: buyer.phone || buyer.mobile || "",
    shippingAddress: shipLines.line,
    city: shipLines.city,
    state: shipLines.state,
    pincode: shipLines.pincode,
    country: shipLines.country,
    billingAddress: billLines.line || shipLines.line,

    fulfillmentCenterId: shipment?.locationId || "",
    dispatchLocation: shipment?.locationId || "",
    shippingProvider: "Marketplace Assigned",
    trackingId: shipment?.trackingId || item?.trackingId || "",
    shippingServiceType:
      shipment?.shipmentType === "EXPRESS" ? "Express" : shipment?.shipmentType === "SELF" ? "Standard" : "Standard",

    itemPrice: pc.sellingPrice ?? pc.customerPrice ?? 0,
    shippingCharge: pc.shippingCharge ?? 0,
    commissionFee: "",
    gstOnCommission: "",
    settlementAmount: "",
    settlementDate: "",

    returnRequestId: "",
    returnType: "Return",
    returnReason: "",
    returnStatus: "Pending",
    replacementOrderId: "",
    orderLineId: item?.orderItemId || "",
    sku: item?.sku || "",
    skuDescription: item?.title || "",
    marketplaceListingId: item?.listingId || "",
    quantityOrdered: item?.quantity ?? "",
    quantityConfirmed: item?.quantity ?? "",
    unitPrice: pc.sellingPrice ?? pc.customerPrice ?? "",
    totalLineValue: pc.totalPrice ?? "",
    taxPercent: "",
    taxAmount: "",
    discount: pc.flipkartDiscount ?? "",
    netPayable: pc.customerPrice ?? pc.totalPrice ?? "",
  };
}
